import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { useAuth } from '@/auth/useAuth';
import { useModules } from '@/modules/ModuleContext';
import { Icon } from '@/ui/Icon';
import { ThemeSwitch } from './ThemeSwitch';

/**
 * Who you are signed in as, and what belongs to you: your account and how the app looks.
 *
 * A disclosure rather than a menu: the panel holds a link and a radio group, and a menu role would
 * promise arrow-key behaviour neither has. The button keeps focus when it opens, so the panel is
 * the next Tab stop; Escape closes it from anywhere inside and puts focus back on the button, and
 * so does leaving it with Tab or pressing outside it. Signing out is not in here on purpose: it
 * sits beside the button, one press away, and never behind another one.
 *
 * @returns the identity button and its panel, or nothing when nobody is signed in
 */
export function IdentityMenu(): React.JSX.Element | null {
  const { user } = useAuth();
  const enabled = useModules();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function closeOnPressOutside(event: PointerEvent): void {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('pointerdown', closeOnPressOutside);
    return () => {
      document.removeEventListener('pointerdown', closeOnPressOutside);
    };
  }, [open]);

  if (user === null) return null;

  return (
    <div
      className="fo-identity"
      ref={root}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
          toggle.current?.focus();
        }
      }}
      onBlur={(event) => {
        // Only a move onto something else closes it. A press on a label inside the panel can
        // report no target at all in Safari, and that must not close the panel mid-click.
        // Pressing inside the panel also moves focus to the nearest focusable ancestor, which is
        // the rail itself (it is the skip link's target), and that is the press, not leaving.
        const next = event.relatedTarget;
        if (!open || !(next instanceof Node)) return;
        const pressedInside =
          event.currentTarget.contains(next) || next.contains(event.currentTarget);
        if (!pressedInside) setOpen(false);
      }}
    >
      <button
        ref={toggle}
        type="button"
        className="fo-identity__toggle"
        data-testid="identity-menu"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${user.displayName}, ${user.workspaceName}, account menu`}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <Icon name="user" className="fo-rail__avatar" />
        {/* The space keeps the two lines two words apart in the text the name is checked
            against; without it they run together and no longer match the name. */}
        <span className="fo-rail__identity-text">
          <span className="fo-rail__identity-name">{user.displayName}</span>{' '}
          <span className="fo-rail__identity-workspace">{user.workspaceName}</span>
        </span>
        <Icon name="chevron-up" className="fo-identity__chevron" />
      </button>

      {open && (
        <div
          className="fo-identity__panel"
          id={panelId}
          role="group"
          aria-label="Account and theme"
        >
          <Link
            className="fo-identity__item"
            to="/settings?tab=account"
            onClick={() => {
              setOpen(false);
            }}
          >
            <Icon name="user" />
            <span>My account</span>
          </Link>
          {enabled('PAYROLL') && (
            <Link
              className="fo-identity__item"
              to="/settings?tab=account&item=pay"
              onClick={() => {
                setOpen(false);
              }}
            >
              <Icon name="payroll" />
              <span>My pay</span>
            </Link>
          )}
          <div className="fo-identity__theme">
            <span className="micro-label" aria-hidden="true">
              Theme
            </span>
            <ThemeSwitch />
          </div>
        </div>
      )}
    </div>
  );
}
